/**
 * VersionThumbnail - Individual thumbnail in version history strip
 *
 * Displays a single version thumbnail with version number badge and
 * timestamp tooltip on hover.
 *
 * Feature: 081-photo-versioning (T044, T047)
 */

import { cn } from '@/lib/utils';
import type { PhotoVersion } from '@/hooks/graphql/usePhotoVersionsGraphQL';

export interface VersionThumbnailProps {
  /** The version data */
  version: PhotoVersion;
  /** Whether this thumbnail is currently selected */
  isSelected?: boolean;
  /** Click handler */
  onClick?: () => void;
  /** Compact mode - smaller thumbnails */
  compact?: boolean;
}

/**
 * Format a date for display in tooltip
 */
function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
}

/**
 * Single version thumbnail component
 */
export function VersionThumbnail({
  version,
  isSelected = false,
  onClick,
  compact = false,
}: VersionThumbnailProps) {
  const thumbnailUrl = version.thumbnailUrl || version.previewUrl;

  return (
    <button
      type="button"
      onClick={onClick}
      title={`Version ${version.versionNumber} - ${formatDate(version.createdAt)}${version.notes ? `\n${version.notes}` : ''}`}
      className={cn(
        'relative flex-shrink-0 rounded overflow-hidden',
        compact ? 'w-12 h-12' : 'w-16 h-16',
        'border-2 transition-all duration-150',
        'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-surface-1',
        isSelected
          ? 'border-primary shadow-lg'
          : 'border-transparent hover:border-gray-500',
        'group'
      )}
    >
      {/* Thumbnail image */}
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt={`Version ${version.versionNumber}`}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full bg-surface-2 flex items-center justify-center">
          <span className="text-xs text-muted">No preview</span>
        </div>
      )}

      {/* Version number badge */}
      <div
        className={cn(
          'absolute bottom-1 right-1 min-w-[20px] h-5 px-1',
          'flex items-center justify-center',
          'text-xs font-medium rounded',
          version.isCurrent
            ? 'bg-primary text-white'
            : 'bg-black/70 text-white'
        )}
      >
        {version.versionNumber}
      </div>

      {/* Current version indicator */}
      {version.isCurrent && (
        <div className="absolute top-1 left-1">
          <div className="w-2 h-2 rounded-full bg-green-500" title="Current version" />
        </div>
      )}

      {/* Hover overlay with timestamp */}
      <div
        className={cn(
          'absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100',
          'transition-opacity duration-150',
          'flex flex-col items-center justify-center text-white text-xs',
          'pointer-events-none'
        )}
      >
        <span className="font-medium">v{version.versionNumber}</span>
        {version.notes && (
          <span className="text-[10px] text-gray-300 truncate max-w-[90%]">
            {version.notes}
          </span>
        )}
      </div>
    </button>
  );
}
